// Magma Front presentation (SKILL FUSIONS; owner 2026-11-03: 'Rework Magma Front, too underwhelming, bad animations').
// Render-only: reads the fusion's wall body (sim.fusion.bodies, BK.wall, f 'magma'), its lava strips (sim.fusion.burns) and
// its 'fuse' cues ('fissure', 'erupt', 'wall', 'splash', 'lavapop'); it never feeds anything back into the simulation.
//
// The cast: the ground splits along a glowing FISSURE across the lane (the Riftline burning-crack decal, flame cards
// licking out of it that grow while it opens, chips and dust shaken off the line). The ERUPTION: magma punches up out of the
// crack into a tall wall (6.5 m: four rows of molten spikes with the shared fire-ramp vertex animation, three rows of
// flipbook flame cards along the crest, the forward-curling fire curtain behind it, a faint heat-haze card row above the
// top edge, dark smoke rolling off the top, embers streaming up, a white-hot molten bead at its foot with a ground glow).
// The ROLL: the sim moves the wall; here it leans into its travel, sheds a trail of sinking spikes behind it, kicks up
// dust and rock chips at its foot, and the LAVA STRIPE it leaves is one flat instanced decal per sim burn strip: a molten
// sheen with glowing cracks that cools yellow → orange → red → dark crust over the strip's life (the burn map is charred
// under every strip once, so the grass stays burnt after the lava skins over). The FINISH: the wall collapses into a
// MAGMA SPLASH (flash and shock rings, a fountain of hot lava blobs and embers, flame cards bursting up, a round lava pool,
// dust, a dent, a shake and a short freeze-frame). LAVA POPS: a burning foe died, a small eruption where it stood.
//
// Everything is pooled and written fresh each frame (no per-frame allocation); emission scales with quality, with the
// crowd-busy dimmer (CROWD.busy) and the particle pool; lights / flash spheres go through the FLASH limiter (Reduce
// flashing); the additive glints read with Glow off because the cel embers and cards are opaque.
import * as THREE from 'three/webgpu';
import { abs, color, float, instancedBufferAttribute, max, mix, smoothstep, time } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import { BK, type FBody } from '../core/fusions';
import { auraTextures } from './auraFlame';
import { CROWD, FLASH, type Fx } from './fx';
import type { GroundGlow } from './groundGlow';
import {
  AMBER, Decals, Embers, FlameCards, ORANGE, PALE, RED, Spikes, Trail, type U, crackMaterial, flatPlane, fract1, hash1, instancedSheet, n2, qOf,
} from './riftline';

const WHITE_HOT = 0xfff4d8, CRUST = 0x1a0806, SMOKE_TONE = 1.7;
const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _e = new THREE.Euler(0, 0, 0, 'YXZ');

/** Lava stripe / pool decal: d = (age 0..1, seed, length m, width m). A molten sheen that skins over as it cools: open lava
 *  (yellow → orange → red with slow flow and flicker) shrinks to a web of glowing cracks between dark crust plates, then the
 *  cracks dim and the whole mark fades out. Flat on the ground; local +z along the strip. */
function lavaMaterial(d: U): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const age = d.x, seed = d.y, L = d.z, W = d.w, q = qOf();
  const px = q.x.mul(W).mul(0.5), pz = q.y.mul(L).mul(0.5); // metres across / along
  const heat = float(1).sub(age).clamp(0, 1); // 1 fresh → 0 cold
  // crust plates: a cellular-ish field from two noises; its ridges are the cracks that stay molten the longest
  const plate = n2(px.mul(1.6).add(seed.mul(7)), pz.mul(1.6).add(seed.mul(3))).mul(0.6).add(n2(px.mul(3.3).add(seed), pz.mul(3.3).add(seed.mul(11))).mul(0.4));
  const crack = float(1).sub(smoothstep(0.0, 0.13, abs(plate))); // 1 on the ridge lines
  // the open lava: everything is molten while fresh; the plates skin over from the inside out as it cools (noise threshold
  // rising with age), leaving the crack web, which thins and dims last
  const skin = smoothstep(float(-0.65).add(age.mul(1.9)), float(-0.35).add(age.mul(1.9)), plate.sub(float(0.15)));
  const open = max(float(1).sub(skin), crack.mul(smoothstep(0.0, 0.25, heat)));
  // flow + flicker: slow pulses sliding along the strip, a fast shimmer
  const flow = n2(pz.mul(0.9).sub(time.mul(1.4)).add(seed), px.mul(0.7)).mul(0.5).add(0.5);
  const flick = n2(px.mul(2.2).add(time.mul(5)), pz.mul(2.2).add(seed)).mul(0.12).add(0.94);
  const hot = heat.mul(flow.mul(0.35).add(0.75)).mul(flick);
  let lava: U = mix(color(RED), color(ORANGE), smoothstep(0.25, 0.5, hot));
  lava = mix(lava, color(AMBER), smoothstep(0.5, 0.75, hot));
  lava = mix(lava, color(PALE), smoothstep(0.82, 1.0, hot).mul(0.8));
  const crustCol = mix(color(CRUST), color(RED).mul(0.45), smoothstep(0.3, 0.9, heat).mul(0.5)); // the fresh crust still glows dully
  m.colorNode = mix(crustCol, lava, open.mul(smoothstep(0.0, 0.06, heat)));
  const ex = float(1).sub(smoothstep(0.7, 1.0, abs(q.x))), ez = float(1).sub(smoothstep(0.82, 1.0, abs(q.y))); // soft edges both ways (pools are short, wide strips)
  const rag = n2(px.mul(2.4), pz.mul(2.4).add(seed)).mul(0.25); // ragged rim
  m.opacityNode = ex.mul(ez).add(rag).clamp(0, 1).mul(float(1).sub(smoothstep(0.86, 1.0, age))); // the dark crust lingers; only the last ~0.5 s fades
  return m;
}

/** instanced lava decals written fresh every frame: d = (age, seed, length, width) */
class LavaStripes {
  readonly mesh: THREE.InstancedMesh; private d: THREE.InstancedBufferAttribute; private n = 0;
  constructor(readonly cap: number) {
    this.d = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); this.d.setUsage(THREE.DynamicDrawUsage);
    this.mesh = new THREE.InstancedMesh(flatPlane(), lavaMaterial(instancedBufferAttribute(this.d)), cap);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 1;
  }
  begin(): void { this.n = 0; }
  /** centre (x, y, z), yaw (local +z along the strip), length L and width W in metres */
  put(x: number, y: number, z: number, yaw: number, L: number, W: number, age: number, seed: number): void {
    if (this.n >= this.cap || age >= 1) return;
    _e.set(0, yaw, 0); _q.setFromEuler(_e); _p.set(x, y, z); _s.set(W / 2, 1, L / 2);
    this.mesh.setMatrixAt(this.n, _m.compose(_p, _q, _s));
    const D = this.d.array as Float32Array, i = this.n * 4; D[i] = age; D[i + 1] = seed; D[i + 2] = L; D[i + 3] = W;
    this.n++;
  }
  end(): void { this.mesh.count = this.n; if (this.n) { this.mesh.instanceMatrix.needsUpdate = true; this.d.needsUpdate = true; } }
}

/** spike rows riding the wall, 5 numbers each: offset along travel (m), height factor, forward lean (rad), half-step shift, teal (0: all molten) */
const ROWS = [0.7, 0.4, 0.8, 0.5, 0, 0.1, 1, 0.42, 0, 0, -0.55, 0.78, 0.25, 0.5, 0, -1.25, 0.5, 0.05, 0.25, 0];
/** flame card rows, 4 numbers each: offset along travel (m), height factor (× wall height), width (m), lean */
const CARD_ROWS = [0.05, 1.35, 2.6, 0.45, -0.75, 1.05, 2.2, 0.32, -1.5, 0.7, 1.9, 0.2];

/** one live (or collapsing) wall, mirroring a sim body by id */
interface Vis { on: boolean; alive: boolean; id: number; t: number; post: number; roll: number; x: number; z: number; x0: number; z0: number; dx: number; dz: number; w: number; H: number; seed: number; trailAcc: number; dustAcc: number; chipAcc: number; emberAcc: number; smokeAcc: number }
/** a transient burst of flame cards (eruption, splash, lava pop) */
interface Puff { on: boolean; t: number; life: number; x: number; z: number; r: number; h: number; n: number; seed: number }
/** a round lava pool (splash, lava pop) */
interface Pool { on: boolean; t: number; life: number; x: number; z: number; r: number; yaw: number; seed: number }

export class MagmaFrontFx {
  private spikes: Spikes; private trail: Trail; private cards: FlameCards; private embers: Embers; private cracks: Decals; private lava: LavaStripes;
  private sheet: THREE.InstancedMesh; private sheetData: THREE.InstancedBufferAttribute;
  private vis: Vis[] = []; private puffs: Puff[] = []; private pools: Pool[] = [];
  /** last seen age of each sim burn strip slot (−1 = free): a strip whose age restarted is a new strip → char the ground once */
  private stripAge = new Float32Array(256).fill(-1);
  private readonly qk: number; private clock = 0; private warm = 3;
  /** freeze-frame the view should apply (taken each frame) */
  hitstop = 0;
  /** QA counters */
  casts = 0; splashes = 0; pops = 0; drawn = 0;

  constructor(scene: THREE.Scene, quality: 'low' | 'medium' | 'high', private fx: Fx, private glow: GroundGlow, private groundAt: (x: number, z: number) => number, private char: (x: number, z: number, r: number) => void, private dent: (x: number, z: number, r: number, d: number) => void) {
    this.qk = quality === 'low' ? 0.5 : quality === 'medium' ? 0.75 : 1;
    const tex = auraTextures();
    this.spikes = new Spikes(Math.round(420 * Math.max(0.7, this.qk)));
    this.trail = new Trail(Math.round(200 * this.qk));
    this.cards = new FlameCards(240, tex);
    this.embers = new Embers(Math.round(380 * Math.max(0.5, this.qk)));
    this.cracks = new Decals(8, flatPlane(), crackMaterial, 2);
    this.lava = new LavaStripes(320);
    const sh = instancedSheet(tex, 4); this.sheet = sh.mesh; this.sheetData = sh.data;
    for (let i = 0; i < 4; i++) this.vis.push({ on: false, alive: false, id: -1, t: 0, post: 0, roll: 0, x: 0, z: 0, x0: 0, z0: 0, dx: 1, dz: 0, w: 8, H: 6.5, seed: 0, trailAcc: 0, dustAcc: 0, chipAcc: 0, emberAcc: 0, smokeAcc: 0 });
    for (let i = 0; i < 12; i++) this.puffs.push({ on: false, t: 0, life: 0.4, x: 0, z: 0, r: 1, h: 1, n: 4, seed: 0 });
    for (let i = 0; i < 16; i++) this.pools.push({ on: false, t: 0, life: 3, x: 0, z: 0, r: 1, yaw: 0, seed: 0 });
    scene.add(this.lava.mesh, this.cracks.mesh, this.spikes.ink, this.spikes.mesh, this.sheet, this.cards.mesh, this.embers.mesh);
  }

  // ------------------------------------------------------------------ cues
  event(ev: Extract<SimEvent, { t: 'fuse' }>): void {
    const fx = this.fx, gy = this.groundAt(ev.x, ev.z), busyK = Math.max(0.3, 1 - CROWD.busy * 0.7);
    switch (ev.k) {
      case 'fissure': { // the ground splits across the lane: the crack decal, a line of chips and dust shaken off it, a glow
        this.casts++;
        const w = ev.r, yaw = Math.atan2(ev.dx, ev.dz), ax = -ev.dz, az = ev.dx;
        this.cracks.add(ev.x, gy + 0.04, ev.z, yaw, w * 0.56, 1.1, 4.5, fract1(ev.x * 0.37 + ev.z * 0.11) * 10, w * 1.12, 1);
        const n = Math.round(w * 1.4 * this.qk * busyK);
        for (let k = 0; k < n; k++) {
          const o = (Math.random() - 0.5) * w, px = ev.x + ax * o, pz = ev.z + az * o;
          if (k % 2 === 0) fx.blast?.chips(px, pz, ax * (Math.random() - 0.5) * 2, az * (Math.random() - 0.5) * 2, 1);
          fx.blast?.dust(px, gy + 0.2, pz, (Math.random() - 0.5) * 1.5, 1.2 + Math.random(), (Math.random() - 0.5) * 1.5, 0.7 + Math.random() * 0.4, 0.5, 1.6, 0.2);
          this.embers.add(px, gy + 0.2, pz, (Math.random() - 0.5) * 2, 3 + Math.random() * 3, (Math.random() - 0.5) * 2, 0.4 + Math.random() * 0.3, 0.1 + Math.random() * 0.06);
        }
        this.glow.flash(ev.x, ev.z, w * 0.5, ORANGE, 0.6, 0.5);
        fx.shake = Math.min(1, fx.shake + 0.06);
        break;
      }
      case 'erupt': { // magma punches up out of the crack: a fountain of embers / hot blobs / dust along the whole line, a flash, a kick
        const w = ev.r, ax = -ev.dz, az = ev.dx, kf = FLASH.gate(true);
        const n = Math.round(w * 2.2 * this.qk * busyK);
        for (let k = 0; k < n; k++) {
          const o = (Math.random() - 0.5) * w * 0.95, px = ev.x + ax * o, pz = ev.z + az * o, e = 1 - (2 * o / w) ** 2;
          this.embers.add(px, gy + 0.3, pz, ev.dx * 2 + (Math.random() - 0.5) * 3, (7 + Math.random() * 7) * (0.5 + 0.5 * e), ev.dz * 2 + (Math.random() - 0.5) * 3, 0.5 + Math.random() * 0.5, 0.14 + Math.random() * 0.1);
          if (k % 3 === 0) fx.blast?.pebbles(px, gy + 0.3, pz, 1, ev.dx * 2, 6 + Math.random() * 5, ev.dz * 2, 2.5, 0.12, 0xffa030, 1.4);
          if (k % 2 === 0) fx.blast?.dust(px, gy + 0.3, pz, ev.dx * 3 + ax * (Math.random() - 0.5) * 3, 2 + Math.random() * 2, ev.dz * 3 + az * (Math.random() - 0.5) * 3, 0.9 + Math.random() * 0.5, 0.8, 2.6, SMOKE_TONE * 0.5);
        }
        this.puff(ev.x - ev.dx * 0.3, ev.z - ev.dz * 0.3, w * 0.5, 2.6, 0.42, 7);
        fx.rings.add(ev.x, gy + 0.15, ev.z, w * 0.5, 0.35, ORANGE, 2.4, 0.9);
        if (kf > 0) { fx.lights.flash(ev.x, gy + 2, ev.z, ORANGE, 40 * Math.max(0.3, kf), 0.3, 18); fx.flashes.add(ev.x, gy + 2, ev.z, 1.6, 0.12, AMBER, 1.1 * kf); }
        fx.stars.add(ev.x, gy + 2.2, ev.z, 2.6, 0.14, WHITE_HOT, 2.6);
        this.glow.flash(ev.x, ev.z, w * 0.7, AMBER, 1.0, 0.45);
        fx.shake = Math.min(1, fx.shake + 0.22); this.hitstop = Math.max(this.hitstop, 0.03);
        break;
      }
      case 'splash': { // the wall collapses into a magma splash: flash, shock rings, lava blobs, flame burst, a pool, dust, a dent
        this.splashes++;
        const R = ev.r, kf = FLASH.gate(true), big = 1 + Math.min(0.5, (R - 4.5) * 0.1);
        fx.stars.add(ev.x, gy + 1.4, ev.z, R * 0.9 * big, 0.2, WHITE_HOT, 3);
        if (kf > 0) { fx.flashes.add(ev.x, gy + 1.2, ev.z, R * 0.35, 0.16, ORANGE, 1.3 * kf); fx.lights.flash(ev.x, gy + 2.5, ev.z, ORANGE, 48 * Math.max(0.3, kf), 0.35, 22); }
        fx.rings.add(ev.x, gy + 0.15, ev.z, R * 1.2, 0.5, ORANGE, 3, 1.2); fx.rings.add(ev.x, gy + 0.2, ev.z, R * 0.9, 0.4, AMBER, 2.2, 0.7); fx.rings.add(ev.x, gy + 0.25, ev.z, R * 0.55, 0.3, WHITE_HOT, 2.6, 0.6);
        const n = Math.round(26 * this.qk * busyK);
        for (let k = 0; k < n; k++) { // the fountain: hot lava blobs arcing out, embers up, dust rolling out
          const a = Math.random() * Math.PI * 2, sp = 3 + Math.random() * 7, up = 6 + Math.random() * 7;
          fx.blast?.pebbles(ev.x, gy + 0.5, ev.z, 1, Math.cos(a) * sp, up, Math.sin(a) * sp, 1.5, 0.22, k % 3 ? 0xffa030 : 0xfff0a0, 2.0);
          this.embers.add(ev.x + Math.cos(a) * 0.5, gy + 0.6, ev.z + Math.sin(a) * 0.5, Math.cos(a) * sp * 0.8, up * 0.9, Math.sin(a) * sp * 0.8, 0.6 + Math.random() * 0.6, 0.14 + Math.random() * 0.1);
          if (k % 2 === 0) fx.blast?.dust(ev.x + Math.cos(a) * R * 0.3, gy + 0.3, ev.z + Math.sin(a) * R * 0.3, Math.cos(a) * R * 2.2, 1 + (k % 3), Math.sin(a) * R * 2.2, 0.9, 0.6, 2.2, SMOKE_TONE * 0.4);
          if (k % 4 === 0) fx.particles.emitDir(ev.x, gy + 1, ev.z, Math.cos(a) * sp, up * 0.6, Math.sin(a) * sp, k % 8 ? AMBER : WHITE_HOT, 0.26, 0.5, 2.8);
        }
        this.puff(ev.x, ev.z, R * 0.55, 4.2, 0.5, 10);
        this.pool(ev.x, ev.z, R * 0.85, 4.5, Math.random() * 6.28);
        this.cracks.add(ev.x, gy + 0.04, ev.z, Math.random() * 6.28, R * 0.6, 0.9, 3.5, fract1(ev.x * 0.13 + ev.z * 0.29) * 10 + 3, R * 1.2, 0.7);
        this.glow.flash(ev.x, ev.z, R * 1.4, ORANGE, 1.0, 0.5);
        this.dent(ev.x, ev.z, R * 0.5, 0.2);
        fx.shake = Math.min(1, fx.shake + 0.4); this.hitstop = Math.max(this.hitstop, 0.06);
        break;
      }
      case 'lavapop': { // a burning foe died: a small eruption where it stood
        this.pops++;
        const R = ev.r;
        fx.rings.add(ev.x, gy + 0.15, ev.z, R * 0.9, 0.3, ORANGE, 2.2, 0.6);
        fx.stars.add(ev.x, gy + 0.9, ev.z, R * 0.6, 0.12, AMBER, 2.4);
        const n = Math.round(7 * this.qk * busyK);
        for (let k = 0; k < n; k++) {
          const a = Math.random() * Math.PI * 2, sp = 2 + Math.random() * 3;
          this.embers.add(ev.x, gy + 0.4, ev.z, Math.cos(a) * sp, 4 + Math.random() * 4, Math.sin(a) * sp, 0.4 + Math.random() * 0.4, 0.1 + Math.random() * 0.08);
          if (k % 3 === 0) fx.blast?.pebbles(ev.x, gy + 0.4, ev.z, 1, Math.cos(a) * sp, 4 + Math.random() * 3, Math.sin(a) * sp, 1, 0.14, 0xffa030, 1.2);
        }
        this.puff(ev.x, ev.z, R * 0.4, 1.8, 0.32, 4);
        this.pool(ev.x, ev.z, R * 0.6, 2.2, Math.random() * 6.28);
        if (FLASH.dim >= 1) fx.lights.flash(ev.x, gy + 1, ev.z, ORANGE, 12, 0.14, 8);
        this.glow.flash(ev.x, ev.z, R, AMBER, 0.6, 0.3);
        break;
      }
      default: break; // 'cast' / 'wall' (the hero's rings and the roll cue) are drawn by fusionFx / read from the body
    }
  }
  private puff(x: number, z: number, r: number, h: number, life: number, n: number): void {
    const p = this.puffs.find((o) => !o.on) ?? this.puffs[0];
    Object.assign(p, { on: true, t: 0, life, x, z, r, h, n, seed: Math.random() });
  }
  private pool(x: number, z: number, r: number, life: number, yaw: number): void {
    let p = this.pools.find((o) => !o.on); if (!p) { p = this.pools[0]; for (const o of this.pools) if (o.t / o.life > p.t / p.life) p = o; }
    Object.assign(p, { on: true, t: 0, life, x, z, r, yaw, seed: Math.random() });
  }

  // ------------------------------------------------------------------ per frame
  update(dt: number, sim: Sim): void {
    this.clock += dt; this.drawn = 0;
    const F = sim.fusion;
    // follow the magma wall bodies by id; new ids appear (the fissure phase), vanished ids collapse
    for (const v of this.vis) if (v.on && v.alive) {
      let found = false;
      for (const b of F.bodies) if (b.alive && b.kind === BK.wall && b.f === 'magma' && b.id === v.id) { found = true; this.follow(v, b); break; }
      if (!found) { v.alive = false; v.post = 0; }
    }
    for (const b of F.bodies) {
      if (!b.alive || b.kind !== BK.wall || b.f !== 'magma') continue;
      let known = false;
      for (const v of this.vis) if (v.on && v.id === b.id) { known = true; break; }
      if (!known) this.adopt(b);
    }
    this.spikes.begin(); this.cards.begin(); this.lava.begin();
    let ns = 0; const SD = this.sheetData.array as Float32Array;
    for (const v of this.vis) {
      if (!v.on) continue;
      if (v.alive) v.t += dt; else { v.post += dt; if (v.post > 0.32) { v.on = false; continue; } }
      ns = this.drawWall(v, dt, ns, SD);
    }
    this.sheet.count = ns; this.sheet.instanceMatrix.needsUpdate = true; this.sheetData.needsUpdate = true;
    this.lavaStripes(sim, dt);
    this.crackFire(dt);
    this.puffsAndPools(dt);
    this.trail.update(dt, this.spikes);
    this.spikes.end(); this.cards.end(); this.lava.end();
    this.embers.update(dt, this.clock);
    this.cracks.update(dt);
    if (this.warm > 0) { // compile pass: one degenerate instance of each layer
      this.warm--;
      for (const o of [this.spikes.mesh, this.sheet, this.cracks.mesh, this.cards.mesh, this.embers.mesh, this.lava.mesh]) if (o.count === 0) { o.setMatrixAt(0, _m.makeScale(0, 0, 0)); o.count = 1; o.instanceMatrix.needsUpdate = true; }
      this.spikes.ink.count = this.spikes.mesh.count;
    }
  }

  private adopt(b: FBody): void {
    let v = this.vis.find((o) => !o.on);
    if (!v) { v = this.vis[0]; for (const o of this.vis) if (o.post > v.post || (!o.alive && v.alive)) v = o; }
    Object.assign(v, { on: true, alive: true, id: b.id, t: 0, post: 0, roll: b.ph > 0 ? 1 : 0, x: b.x, z: b.z, x0: b.x0, z0: b.z0, dx: b.dx, dz: b.dz, w: b.w, H: 6.5 * Math.min(1.25, Math.max(0.8, b.w / 8.5)), seed: (b.id * 0.618) % 1, trailAcc: 0, dustAcc: 0, chipAcc: 0, emberAcc: 0, smokeAcc: 0 });
  }
  private follow(v: Vis, b: FBody): void {
    v.x = b.x; v.z = b.z; v.w = b.w;
    if (b.ph > 0 && v.roll === 0) { v.roll = 1; v.t = 0; } // the eruption: the wall's own clock restarts as it rises
  }

  /** the fissure (roll 0: flame cards growing out of the crack line while it opens) and the wall (roll 1) */
  private drawWall(v: Vis, dt: number, ns: number, SD: Float32Array): number {
    const { dx, dz, w } = v, ax = -dz, az = dx, yaw = Math.atan2(dx, dz), busyK = Math.max(0.35, 1 - CROWD.busy * 0.7);
    this.drawn++;
    if (v.roll === 0 && v.alive) { // the crack is opening: low magma tongues boil up along the line, growing; a hot glow spreads
      const grow = Math.min(1, v.t / 0.3), nc = Math.max(4, Math.ceil(w / 1.1));
      for (let j = 0; j < nc; j++) {
        const r = hash1(j * 4.1 + v.seed * 31), o = ((j + 0.5) / nc - 0.5) * w * 0.95 + (r - 0.5) * 0.5, px = v.x0 + ax * o, pz = v.z0 + az * o;
        this.cards.put(px, this.groundAt(px, pz) - 0.15, pz, 1.2 + r * 0.5, (0.5 + 1.3 * grow) * (0.7 + 0.5 * r), fract1(r + v.seed), Math.min(1, grow * 2), 0.6 + 0.4 * grow, 0.3, 0.5, 0.6);
      }
      this.glow.strip(v.x0 - ax * w * 0.5, v.z0 - az * w * 0.5, v.x0 + ax * w * 0.5, v.z0 + az * w * 0.5, this.groundAt(v.x0, v.z0) + 0.3, 1.0 + grow, ORANGE, 0.5 + 0.6 * grow, 1);
      return ns;
    }
    // eruption: spikes punch up with an overshoot in ~0.14 s; collapse: they sink in ~0.26 s
    const te = Math.min(1, v.t / 0.14), env = v.alive ? (te < 1 ? 1 - (1 - te) ** 3 * (1 - 2.4 * te) : 1) : Math.max(0, 1 - v.post / 0.26) ** 1.5;
    const H = v.H * env, n = Math.max(6, Math.round(w / 0.5)), step = w / n, surge = 0.12 * Math.sin(this.clock * 9 + v.seed * 6); // the wall breathes forward as it rolls
    for (let ri = 0; ri < ROWS.length; ri += 5) for (let j = 0; j < n; j++) {
      const off = ROWS[ri] + surge, hk = ROWS[ri + 1], lean = ROWS[ri + 2], sh = ROWS[ri + 3];
      const r = hash1(j * 3.1 + off * 17 + v.seed * 91), o = ((j + 0.5 + sh * (r - 0.5)) / n - 0.5) * w;
      const e = 1 - (2 * o / w) ** 2, taper = 0.35 + 0.65 * Math.sqrt(Math.max(0, e));
      const px = v.x + ax * o + dx * off, pz = v.z + az * o + dz * off;
      this.spikes.put(px, this.groundAt(px, pz) - 0.05, pz, yaw + (r - 0.5) * 0.5, lean + (r - 0.5) * 0.2, step * (1.4 + 0.35 * r), H * hk * 0.8 * (0.62 + 0.38 * r) * taper, 0.85 + 0.3 * r, 0, 1, r);
    }
    // living fire: flame card rows riding the crest, the leading row leaning forward over the foot like a breaking wave
    const calpha = v.alive ? Math.min(1, v.t / 0.05) : Math.max(0, 1 - v.post / 0.3);
    const cH = v.H * (v.alive ? env : Math.max(0, 1 - v.post / 0.3));
    for (let ri = 0; ri < CARD_ROWS.length; ri += 4) {
      const off = CARD_ROWS[ri] + surge, hk = CARD_ROWS[ri + 1], cw = CARD_ROWS[ri + 2], lean = CARD_ROWS[ri + 3];
      const nc = Math.max(4, Math.ceil(w / (cw * 0.62)));
      for (let j = 0; j < nc; j++) {
        const r = hash1(j * 5.3 + ri * 11 + v.seed * 57), o = ((j + 0.5) / nc - 0.5) * w + (r - 0.5) * 0.4;
        const e = 1 - (2 * o / w) ** 2, taper = 0.3 + 0.7 * Math.sqrt(Math.max(0, e));
        const px = v.x + ax * o + dx * off, pz = v.z + az * o + dz * off;
        this.cards.put(px, this.groundAt(px, pz) - 0.1, pz, cw, cH * hk * (0.8 + 0.35 * r) * taper, fract1(r + v.seed), calpha, 1, lean, 0.38, 0.85);
      }
    }
    // heat haze: a faint, wide, very swaying row above the top edge (translucent, dim heat) so the air above the wall wobbles
    if (v.alive) { const nh = Math.max(3, Math.ceil(w / 2.2)); for (let j = 0; j < nh; j++) { const r = hash1(j * 7.7 + v.seed * 13), o = ((j + 0.5) / nh - 0.5) * w, px = v.x + ax * o - dx * 0.6, pz = v.z + az * o - dz * 0.6; this.cards.put(px, this.groundAt(px, pz) + cH * 0.75, pz, 2.8, cH * 0.7 * (0.8 + 0.4 * r), fract1(r * 3 + v.seed), 0.22 * calpha, 0.12, 0.2, 0.9, 0.5); } }
    // the curling fire curtain riding just behind the crest (deeper curl than the Riftline's: it breaks over its own foot)
    if (ns < 4) {
      const gy = this.groundAt(v.x, v.z);
      _e.set(0, yaw, 0); _q.setFromEuler(_e); _p.set(v.x - dx * (0.8 - surge), gy - 0.05, v.z - dz * (0.8 - surge)); _s.set(w * 1.04, H * 1.12, 3.0);
      this.sheet.setMatrixAt(ns, _m.compose(_p, _q, _s));
      SD[ns * 4] = v.alive ? Math.min(1, v.t / 0.06) : Math.max(0, 1 - v.post / 0.2); SD[ns * 4 + 1] = v.seed * 10 + 5; SD[ns * 4 + 2] = w; SD[ns * 4 + 3] = 0;
      ns++;
    }
    // the molten bead at the foot (a fresh lava strip under the wall) with a hot ground glow
    const hw = w / 2, gyb = this.groundAt(v.x, v.z);
    this.lava.put(v.x - dx * 0.3, gyb + 0.06, v.z - dz * 0.3, yaw + Math.PI / 2, w * 1.02, 1.6, 0.02 + (v.alive ? 0 : v.post), v.seed * 10 + 1);
    this.glow.strip(v.x - ax * hw, v.z - az * hw, v.x + ax * hw, v.z + az * hw, gyb + 0.5, 1.6, AMBER, 1.1 * env, 1);
    if (!v.alive) return ns;
    // rolling tail: world-fixed spikes dropped at the wall face that sink behind it (reads as rolling)
    v.trailAcc += dt;
    if (v.trailAcc >= 0.035) {
      v.trailAcc = 0;
      const k = Math.round(n * 0.45 * this.qk * busyK);
      for (let i = 0; i < k; i++) {
        const o = (Math.random() - 0.5) * w * 0.95, e = 1 - (2 * o / w) ** 2, taper = 0.35 + 0.65 * Math.sqrt(Math.max(0, e));
        const px = v.x + ax * o - dx * 0.3, pz = v.z + az * o - dz * 0.3;
        this.trail.add(px, this.groundAt(px, pz) - 0.05, pz, yaw + (Math.random() - 0.5) * 0.25, 0.35, step * 1.5, v.H * (0.4 + Math.random() * 0.3) * taper, 0.85, 0.18 + Math.random() * 0.08, 0);
      }
    }
    // dust rolling ahead of the foot, rock chips thrown forward, smoke rolling off the top
    v.dustAcc += dt * 36 * this.qk * busyK;
    while (v.dustAcc >= 1) {
      v.dustAcc -= 1;
      const o = (Math.random() - 0.5) * w, px = v.x + ax * o + dx * 0.5, pz = v.z + az * o + dz * 0.5;
      this.fx.blast?.dust(px, this.groundAt(px, pz) + 0.25, pz, dx * (9 + Math.random() * 4) + ax * (Math.random() - 0.5) * 2, 0.8 + Math.random(), dz * (9 + Math.random() * 4) + az * (Math.random() - 0.5) * 2, 0.6 + Math.random() * 0.35, 0.5, 1.9, Math.random() * 0.35);
    }
    v.chipAcc += dt * 20 * this.qk * busyK;
    while (v.chipAcc >= 1) { v.chipAcc -= 1; const o = (Math.random() - 0.5) * w; this.fx.blast?.chips(v.x + ax * o + dx * 0.5, v.z + az * o + dz * 0.5, dx, dz, 1); }
    v.smokeAcc += dt * 9 * this.qk * busyK;
    while (v.smokeAcc >= 1) {
      v.smokeAcc -= 1;
      const o = (Math.random() - 0.5) * w * 0.9, px = v.x + ax * o - dx * 1.2, pz = v.z + az * o - dz * 1.2;
      this.fx.blast?.dust(px, this.groundAt(px, pz) + v.H * 0.9, pz, -dx * 2 + ax * (Math.random() - 0.5) * 1.5, 2.2 + Math.random() * 1.5, -dz * 2 + az * (Math.random() - 0.5) * 1.5, 1.6 + Math.random() * 0.8, 1.3, 3.6, SMOKE_TONE);
    }
    // embers streaming up off the crest (opaque cel sparks) plus a few additive glints for the Glow setting
    v.emberAcc += dt * 80 * this.qk * busyK;
    while (v.emberAcc >= 1) {
      v.emberAcc -= 1;
      const o = (Math.random() - 0.5) * w * 0.95, px = v.x + ax * o - dx * 0.4, pz = v.z + az * o - dz * 0.4, gy = this.groundAt(px, pz);
      this.embers.add(px, gy + v.H * (0.5 + Math.random() * 0.8), pz, dx * (6 + Math.random() * 6) + ax * (Math.random() - 0.5) * 2, 3 + Math.random() * 5, dz * (6 + Math.random() * 6) + az * (Math.random() - 0.5) * 2, 0.45 + Math.random() * 0.5, 0.12 + Math.random() * 0.1);
      if (Math.random() < 0.25) this.fx.particles.emitDir(px, gy + v.H * (0.7 + Math.random() * 0.4), pz, dx * 10 + (Math.random() - 0.5) * 3, 3 + Math.random() * 3, dz * 10 + (Math.random() - 0.5) * 3, AMBER, 0.28, 0.3, 3);
    }
    return ns;
  }

  /** the lava stripe: one decal per live sim burn strip of this fusion (age = its own clock), charring the ground once each */
  private lavaStripes(sim: Sim, dt: number): void {
    const B = sim.fusion.burns, SA = this.stripAge, busyK = Math.max(0.3, 1 - CROWD.busy);
    let emb = 0; const embMax = Math.round(3 * this.qk * busyK);
    for (let i = 0; i < B.length; i++) {
      const b = B[i];
      if (!b.alive || b.f !== 'magma') { SA[i] = -1; continue; }
      const fresh = SA[i] < 0 || b.t < SA[i]; SA[i] = b.t;
      const mx = (b.x0 + b.x1) / 2, mz = (b.z0 + b.z1) / 2, ddx = b.x1 - b.x0, ddz = b.z1 - b.z0, L = Math.hypot(ddx, ddz);
      if (L < 0.05) continue;
      if (fresh) { const k = Math.max(1, Math.round(L / 2.4)); for (let j = 0; j < k; j++) { const u = (j + 0.5) / k; this.char(b.x0 + ddx * u, b.z0 + ddz * u, Math.max(1.0, Math.min(2.2, L / k * 0.7))); } }
      const age = b.t / b.life, W = b.w * 2 * 1.15; // (w is the strip's half width)
      this.lava.put(mx, this.groundAt(mx, mz) + 0.05, mz, Math.atan2(ddx, ddz), L + W * 0.3, W, age, fract1(i * 0.137 + 0.3));
      // embers and a little smoke rising off the fresh lava
      if (age < 0.45 && emb < embMax && Math.random() < dt * 2.5 * (1 - age)) { emb++; const u = Math.random(); const px = b.x0 + ddx * u, pz = b.z0 + ddz * u; this.embers.add(px, this.groundAt(px, pz) + 0.2, pz, (Math.random() - 0.5), 1.5 + Math.random() * 2, (Math.random() - 0.5), 0.5 + Math.random() * 0.5, 0.08 + Math.random() * 0.06); }
    }
  }

  /** flame cards licking along every hot crack (they shrink and redden as it cools) and embers rising off it */
  private crackFire(dt: number): void {
    const busyK = Math.max(0.3, 1 - CROWD.busy);
    for (let ci = 0; ci < this.cracks.live; ci++) {
      const it = this.cracks.items[ci], u = it.t / it.life, heat = it.c * Math.max(0, 1 - u * 1.05);
      if (heat <= 0.03) continue;
      const ax = Math.cos(it.yaw), az = -Math.sin(it.yaw), fx = Math.sin(it.yaw), fz = Math.cos(it.yaw), L = it.sx * 2 * 0.9, nc = Math.max(3, Math.round(L / 0.9));
      const grow = Math.min(1, it.t / 0.12), alpha = Math.min(1, heat * 3) * grow;
      for (let k = 0; k < nc; k++) {
        const r = hash1(k * 7.7 + it.a * 13), r2 = hash1(k * 3.3 + it.a * 7 + 1), o = ((k + 0.5) / nc - 0.5) * L + (r - 0.5) * 0.45, off = (r2 - 0.5) * 0.45;
        const e = 1 - (2 * o / L) ** 2, px = it.x + ax * o + fx * off, pz = it.z + az * o + fz * off;
        const h = (0.7 + 1.0 * heat) * (0.6 + 0.7 * r2) * (0.4 + 0.6 * Math.sqrt(Math.max(0, e))) * grow;
        this.cards.put(px, this.groundAt(px, pz) - 0.22, pz, 1.3, h + 0.22, fract1(r + it.a * 0.31), alpha, 0.35 + 0.65 * heat, 0.45, 0.3, 0.55);
      }
      if (Math.random() < dt * 14 * heat * this.qk * busyK) {
        const o = (Math.random() - 0.5) * L, px = it.x + ax * o, pz = it.z + az * o;
        this.embers.add(px, this.groundAt(px, pz) + 0.3 + Math.random() * 0.5, pz, (Math.random() - 0.5) * 1.5, 2 + Math.random() * 3, (Math.random() - 0.5) * 1.5, 0.6 + Math.random() * 0.5, 0.1 + Math.random() * 0.07);
      }
    }
  }

  /** transient flame-card bursts (eruption / splash / pops) and round lava pools */
  private puffsAndPools(dt: number): void {
    for (const p of this.puffs) {
      if (!p.on) continue;
      p.t += dt; const u = p.t / p.life; if (u >= 1) { p.on = false; continue; }
      const rise = Math.min(1, u * 4), fade = 1 - u * u;
      for (let k = 0; k < p.n; k++) {
        const r = hash1(k * 9.1 + p.seed * 41), a = r * 6.283, d = p.r * (0.2 + 0.8 * hash1(k * 3.7 + p.seed * 17)), px = p.x + Math.cos(a) * d, pz = p.z + Math.sin(a) * d;
        this.cards.put(px, this.groundAt(px, pz) - 0.1 + u * 1.2, pz, 1.1 + r * 0.8, p.h * rise * (0.6 + 0.6 * r) * (1 - u * 0.5), fract1(r + p.seed), fade, 1, 0.3, 0.5, 0.7);
      }
    }
    for (const p of this.pools) {
      if (!p.on) continue;
      p.t += dt; const u = p.t / p.life; if (u >= 1) { p.on = false; continue; }
      const grow = Math.min(1, p.t / 0.15);
      this.lava.put(p.x, this.groundAt(p.x, p.z) + 0.055, p.z, p.yaw, p.r * 2 * grow, p.r * 1.7 * grow, u, p.seed * 10 + 2);
    }
  }

  clear(): void {
    for (const v of this.vis) { v.on = false; v.alive = false; }
    for (const p of this.puffs) p.on = false; for (const p of this.pools) p.on = false;
    this.stripAge.fill(-1);
    this.trail.clear(); this.cracks.clear(); this.embers.clear();
    this.spikes.begin(); this.spikes.end(); this.cards.begin(); this.cards.end(); this.lava.begin(); this.lava.end(); this.sheet.count = 0;
  }
}
